import { useCallback, useEffect, useState } from 'react';
import useHotkeys from '@reecelucas/react-use-hotkeys';
import type { EmblaCarouselType } from 'embla-carousel';

import { CarouselNextButton, CarouselPrevButton } from './CarouselButtons';

type CarouselArrowNavigationProps = {
  emblaApi?: EmblaCarouselType;
  isMobileVisible?: boolean;
};

export function CarouselArrowNavigation({
  emblaApi,
  isMobileVisible = false,
}: CarouselArrowNavigationProps) {
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  return (
    <>
      <CarouselPrevButton
        disabled={prevBtnDisabled}
        iconOnly
        isAbsolute
        isMobileVisible={isMobileVisible}
        onClick={onPrevButtonClick}
      />

      <CarouselNextButton
        disabled={nextBtnDisabled}
        iconOnly
        isAbsolute
        isMobileVisible={isMobileVisible}
        onClick={onNextButtonClick}
      />
    </>
  );
}

export function usePrevNextButtons(emblaApi: EmblaCarouselType | undefined) {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  useHotkeys('ArrowLeft', () => {
    if (!prevBtnDisabled) onPrevButtonClick();
  });
  useHotkeys('ArrowRight', () => {
    if (!nextBtnDisabled) onNextButtonClick();
  });

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
}
